<template>
 <form @submit.prevent="handleSubmit" id="asset_edit_form" enctype="multipart/form-data">

  <LoadingBar v-if="loading" message="Loading Asset Details..." />

  <div v-else>

    <!-- Asset Name -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Asset Name <span class="text-danger">*</span></label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="name"
        name="name"
        ref="name"
        v-model="form.name"
        placeholder="Asset Name"
      />
    </div>

    <!-- Category -->
    <div class="mb-4">
      <MultiSelect
        v-model="form.category"
        id="category"
        name="category"
        ref="category"
        :multiple="false"
        :options="categories"
        label-key="name"
        value-key="external_id"
        placeholder="Select Category"
        label="Category"
      />
    </div>

    <!-- Code -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Code <span class="text-danger">*</span></label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="code"
        name="code"
        ref="code"
        v-model="form.code"
        placeholder="Asset Code"
      />
    </div>

    <!-- Purchase Date -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Purchase Date <span class="text-danger">*</span></label>
      <input
        type="date"
        class="form-control form-control-thin"
        id="purchase_date"
        name="purchase_date"
        ref="purchase_date"
        v-model="form.purchase_date"
      />
    </div>

    <!-- Vendors -->
    <div class="mb-4">
      <MultiSelect
        v-model="form.vendor_ids"
        id="vendor_ids"
        name="vendor_ids"
        ref="vendor_ids"
        :options="vendors"
        placeholder="Select vendors…"
        label="Vendors"
      />
    </div>

    <!-- Materials -->
   <div class="mb-4">
  <MultiSelect
    v-model="form.material_ids"
    id="material_ids"
    name="material_ids"
    ref="material_ids"
    :multiple="true"
    :groups="dropdownCategories"
    group-label-key="name"
    group-children-key="materials"
    child-value-key="id"
    child-label-key="name"
    placeholder="Select materials…"
    label="Materials"
  />
</div>

    <!-- Criticality -->
    <div class="mb-4">
      <MultiSelect
        v-model="form.criticality"
        id="criticality"
        name="criticality"
        ref="criticality"
        :multiple="false"
        label="Criticality"
      >
        <option value="">Select Criticality</option>
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
      </MultiSelect>
    </div>

    <!-- Serial Number -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Serial Number <span class="text-danger">*</span></label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="serial_number"
        name="serial_number"
        ref="serial_number"
        v-model="form.serial_number"
        placeholder="Serial Number"
      />
    </div>

    <!-- Model -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Model <span class="text-danger">*</span></label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="model"
        name="model"
        ref="model"
        v-model="form.model"
        placeholder="Model"
      />
    </div>

    <!-- Manufacturer -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Manufacturer <span class="text-danger">*</span></label>
      <input
        type="text"
        class="form-control form-control-thin"
        id="manufacturer"
        name="manufacturer"
        ref="manufacturer"
        v-model="form.manufacturer"
        placeholder="Manufacturer"
      />
    </div>

    <!-- Description -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Description (If Any)</label>
      <textarea
        class="form-control form-control-thin"
        id="description"
        name="description"
        ref="description"
        v-model="form.description"
        rows="3"
        placeholder="Asset Description"
      ></textarea>
    </div>

    <!-- Location -->
    <div class="mb-4">
      <MultiSelect
        v-model="form.location"
        id="location"
        name="location"
        ref="location"
        :multiple="false"
        :options="locations"
        label-key="name"
        value-key="external_id"
        placeholder="Select Location"
        label="Location"
      />
    </div>

    <!-- Photo -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Photo</label>

      <input
        type="file"
        class="form-control form-control-thin"
        id="photo"
        name="photo"
        ref="photo"
        @change="onPhotoChange"
        accept="image/*"
      />

      <div v-if="form.photo_preview" class="mt-2">
        <img
          :src="form.photo_preview"
          style="max-width:120px;max-height:120px;border-radius:6px;border:1px solid #e5e7eb;"
        />

        <button
          type="button"
          class="btn btn-link btn-sm text-danger p-0 mt-1"
          @click="clearPhoto"
        >
          <i class="fa fa-times"></i> Clear
        </button>
      </div>
    </div>

    <!-- Attachment -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Attachment File</label>

      <input
        type="file"
        class="form-control form-control-thin"
        id="file"
        name="file"
        ref="file"
        @change="onFileChange"
      />

      <small class="text-muted">Max size: 20MB</small>
    </div>

    <!-- Buttons -->
    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">
        <i class="fa fa-times"></i> Cancel
      </button>

      <button
        type="submit"
        class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
        :disabled="submitting"
      >
        <i class="fa fa-save"></i>
        {{ submitting ? 'Saving…' : 'Save Changes' }}
      </button>
    </div>

  </div>
</form>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'

export default {
  name: 'EditAsset',
  emits: ['updated', 'close'],
  components: { MultiSelect, LoadingBar },

  props: {
    assetId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      form: {
        name: '',
        category: '',
        code: '',
        purchase_date: '',
        criticality: '',
        serial_number: '',
        model: '',
        manufacturer: '',
        description: '',
        location: '',
        photo: null,
        photo_preview: null,
        existing_photo: null,
        file: null,
        existing_file: null,
        vendor_ids: [],
        material_ids: [],
        remove_photo: false,
        remove_file: false,
      },
      submitting: false,
      loading: false,
      categories: [],
      locations: [],
      vendors: [],
      dropdownCategories: [],
    }
  },

  mounted() {
    this.fetchAssetDetails()
    this.fetchCategories()
    this.fetchLocations()
    this.fetchVendors()
    this.fetchDropdownMaterials()
  },

  methods: {
    // ── Fetch Asset Details ──────────────────────────────────────────────────
    async fetchAssetDetails() {
      this.loading = true
      
      try {
        const result = await form_api_submit({
          url: `asset/show/${this.assetId}`,
          method: 'GET',
          data: {},
          show_success_toast: false,
          show_error_toast: true,
          remove_pre_toasts: false,
        })

        if (result.success) {
          this.populateForm(result.data)
        } 
      } catch (error) {
        console.error('Error fetching asset:', error)
      } finally {
        this.loading = false
      }
    },

    // ── Populate Form ────────────────────────────────────────────────────────
    populateForm(asset) {
      this.form.name = asset.name || ''
      this.form.code = asset.code || ''
      this.form.purchase_date = asset.purchase_date ? asset.purchase_date.substring(0, 10) : ''
      this.form.criticality = asset.criticality || ''
      this.form.serial_number = asset.serial_number || ''
      this.form.model = asset.model || ''
      this.form.manufacturer = asset.manufacturer || ''
      this.form.description = asset.description || ''

      this.form.category = asset.get_category?.external_id || 
                          asset.asset_category?.external_id || 
                          asset.category?.external_id || 
                          ''

      this.form.location = asset.get_location?.external_id || 
                          asset.location?.external_id || 
                          ''

      this.form.vendor_ids = (asset.get_vendors || []).map(v => v.id)
      this.form.material_ids = (asset.get_materials || []).map(m => m.id)

      this.form.existing_photo = asset.photo || null
      this.form.existing_file = asset.file || null

      this.form.remove_photo = false
      this.form.remove_file = false
    },

    // ── Photo handlers ──────────────────────────────────────────────────────
    onPhotoChange(e) {
      const file = e.target.files[0]
      if (!file) return
      
      if (file.size > 10 * 1024 * 1024) {
        e.target.value = ''
        return
      }
      
      this.form.photo = file
      this.form.photo_preview = URL.createObjectURL(file)
      this.form.remove_photo = false
    },

    clearPhoto() {
      this.form.photo = null
      this.form.photo_preview = null
      if (this.form.existing_photo) {
        this.form.remove_photo = true
      }
    },

    removeExistingPhoto() {
      this.form.existing_photo = null
      this.form.remove_photo = true
    },

    // ── File handlers ────────────────────────────────────────────────────────
    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      
      if (file.size > 20 * 1024 * 1024) {
        e.target.value = ''
        return
      }
      
      this.form.file = file
      this.form.remove_file = false
    },

    removeExistingFile() {
      this.form.existing_file = null
      this.form.remove_file = true
    },

    // ── File URL helper ──────────────────────────────────────────────────────
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (typeof filePath === 'string' && filePath.startsWith('http')) return filePath
      const base = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${base}/${filePath}`
    },

    // ── Submit ──────────────────────────────────────────────────────────────
    async handleSubmit() {
      this.submitting = true
      this.errors = {}

      try {
        const fd = new FormData()

        const scalars = [
          'name',
          'code',
          'purchase_date',
          'criticality',
          'serial_number',
          'model',
          'manufacturer',
          'description',
          'location',
          'category'
        ]

        scalars.forEach(key => {
          if (this.form[key] !== null && this.form[key] !== '') {
            fd.append(key, this.form[key])
          }
        })

        if (this.form.vendor_ids?.length) {
          this.form.vendor_ids.forEach(id => {
            fd.append('vendor_ids[]', id)
          })
        }

        if (this.form.material_ids?.length) {
          this.form.material_ids.forEach(id => {
            fd.append('material_ids[]', id)
          })
        }

        if (this.form.photo) {
          fd.append('photo', this.form.photo)
        } else if (this.form.remove_photo) {
          fd.append('remove_photo', '1')
        }

        if (this.form.file) {
          fd.append('file', this.form.file)
        } else if (this.form.remove_file) {
          fd.append('remove_file', '1')
        }

        const result = await form_api_submit({
          url: `asset/update/${this.assetId}`, 
          method: 'POST',
          data: fd,
          curr_form_id: '#asset_edit_form'
        })

        if (result.success) {
          this.$emit('updated')
          this.close()
        } else if (result.errors) {
          this.errors = result.errors
        }

      } catch (error) {
        console.error('Error updating asset:', error)
      } finally {
        this.submitting = false
      }
    },

    // ── Dropdown fetchers ──────────────────────────────────────────────────
    async fetchCategories() {
      try {
        const result = await form_api_submit({
          url: 'asset/category/dropdown',
          method: 'GET',
          data: {},
          show_success_toast: false,
          show_error_toast: true,
        })
        if (result.success) {
          this.categories = result.data || []
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    },

    async fetchLocations() {
      try {
        const result = await form_api_submit({
          url: 'locations/dropdown',
          method: 'GET',
          data: {},
          show_success_toast: false,
          show_error_toast: true,
        })
        if (result.success) {
          this.locations = result.data || []
        }
      } catch (error) {
        console.error('Error fetching locations:', error)
      }
    },

    async fetchVendors() {
      try {
        const result = await form_api_submit({
          url: 'vendor/get-vendors',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })
        if (result?.success) {
          this.vendors = (result.data || []).map(v => ({
            value: v.id,
            label: v.first_name || v.get_profile?.company_name || 'Vendor',
          }))
        }
      } catch (error) {
        console.error('Error fetching vendors:', error)
      }
    },

async fetchDropdownMaterials() {
  try {
    const result = await form_api_submit({
      url: 'materials/dropdown',
      method: 'GET',
      show_success_toast: false,
      show_error_toast: false,
    })

    if (result?.success) {
      this.dropdownCategories = Object.values(
        (result.data || []).reduce((groups, material) => {
          const type = material.type || 'Other'

          if (!groups[type]) {
            groups[type] = {
              name: type.charAt(0).toUpperCase() + type.slice(1),
              materials: []
            }
          }

          groups[type].materials.push(material)

          return groups
        }, {})
      )
    }
  } catch (error) {
    console.error('Error fetching materials:', error)
  }
},
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.existing-file {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.existing-file-link {
  font-size: 12px;
  color: #4f46e5;
  text-decoration: none;
}

.existing-file-link:hover {
  text-decoration: underline;
}

.existing-file-actions {
  display: flex;
  align-items: center;
}
.modal-footer {
  padding: 16px 0 0 0;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.hover-white:hover {
  color: #ffffff !important;
}
</style>